import { useState, useEffect, useRef } from 'react';
import { Platform } from 'react-native';

/**
 * How long the browser must report 'offline' before we treat it as a genuine
 * disconnection.  navigator.onLine fires 'offline' transiently during network
 * switches and brief dropouts, even when the API is still reachable.  This
 * debounce prevents those sub-2-second blips from triggering the autosave
 * error dialog (FLARE-44).
 */
const OFFLINE_DEBOUNCE_MS = 2000;

/**
 * Hook to monitor network connectivity.
 * Currently implemented for Web using navigator.onLine.
 * For Native, it defaults to true (online) as @react-native-community/netinfo is not installed.
 *
 * The offline state is debounced: we only report offline after the browser has
 * sustained an 'offline' event for OFFLINE_DEBOUNCE_MS.  Recovering 'online'
 * cancels any pending transition immediately.
 */
export function useNetworkStatus() {
  // Default to true to assume online until proven otherwise
  const [isOnline, setIsOnline] = useState(true);
  const offlineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }

    // Initial check
    if (typeof navigator !== 'undefined') {
      setIsOnline(navigator.onLine);
    }

    const handleOnline = () => {
      if (offlineTimerRef.current !== null) {
        clearTimeout(offlineTimerRef.current);
        offlineTimerRef.current = null;
      }
      setIsOnline(true);
    };

    const handleOffline = () => {
      offlineTimerRef.current = setTimeout(() => {
        offlineTimerRef.current = null;
        setIsOnline(false);
      }, OFFLINE_DEBOUNCE_MS);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (offlineTimerRef.current !== null) {
        clearTimeout(offlineTimerRef.current);
      }
    };
  }, []);

  return { isOnline, isOffline: !isOnline };
}
